import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
} from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import api from "../../config/api";
import { useAppDialog } from "./AppDialogProvider";

const TABLE_HEIGHT = 540;
const PREVIEW_TABLE_HEIGHT = 620;
const ROW_HEIGHT = 53;
const OVERSCAN = 10;

export default function PrintPage({ onBack }) {
  const dialog = useAppDialog();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKey, setSelectedRowKey] = useState("");

  const [reportRows, setReportRows] = useState([]);
  const [reportTitle, setReportTitle] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [printing, setPrinting] = useState(false);

  const [scrollTop, setScrollTop] = useState(0);

  const searchInputRef = useRef(null);
  const previewPrintRef = useRef(null);
  const tableScrollRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingScrollToKeyRef = useRef("");

  const releaser = true;

  const deferredSearchText = useDeferredValue(searchText);

  const rowByKey = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      map.set(row._key, row);
    }
    return map;
  }, [rows]);

  const selectedRow = useMemo(() => {
    return rowByKey.get(selectedRowKey) || null;
  }, [rowByKey, selectedRowKey]);

  const selectedPart = useMemo(() => {
    if (!selectedRow) {
      return {
        partNum: "",
        rev: 0,
        revMinor: 0,
      };
    }

    return {
      partNum: String(selectedRow.partNumber ?? ""),
      rev: Number(selectedRow.printRevision ?? 0),
      revMinor: Number(selectedRow.mafiaRevision ?? 0),
    };
  }, [selectedRow]);

  const filteredRows = useMemo(() => {
    const search = deferredSearchText.trim().toUpperCase();
    if (!search) return rows;
    return rows.filter((row) => row._search.includes(search));
  }, [rows, deferredSearchText]);

  const totalRows = filteredRows.length;
  const totalHeight = totalRows * ROW_HEIGHT;

  const visibleRange = useMemo(() => {
    const viewportHeight = TABLE_HEIGHT;
    const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const endIndex = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN
    );

    return {
      startIndex,
      endIndex,
    };
  }, [scrollTop, totalRows]);

  const visibleRows = useMemo(() => {
    return filteredRows.slice(visibleRange.startIndex, visibleRange.endIndex);
  }, [filteredRows, visibleRange]);

  const topSpacerHeight = visibleRange.startIndex * ROW_HEIGHT;
  const bottomSpacerHeight = Math.max(
    0,
    totalHeight - topSpacerHeight - visibleRows.length * ROW_HEIGHT
  );

  const loadAllParts = useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) {
          setRefreshing(true);
        }

        const data = await getAllParts();
        if (!mountedRef.current) return;

        setRows(data || []);

        if (selectedRowKey) {
          const stillExists = (data || []).some((row) => row._key === selectedRowKey);
          if (!stillExists) {
            setSelectedRowKey("");
          }
        }
      } catch (error) {
        console.error("Failed to load all parts:", error);
        await dialog.error("Failed to load all parts.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          if (showSpinner) {
            setRefreshing(false);
          }
        }
      }
    },
    [selectedRowKey, dialog]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadAllParts(false);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [loadAllParts]);

  useEffect(() => {
    const targetKey = pendingScrollToKeyRef.current;
    if (!targetKey) return;

    const index = filteredRows.findIndex((row) => row._key === targetKey);
    if (index < 0) {
      pendingScrollToKeyRef.current = "";
      return;
    }

    const container = tableScrollRef.current;
    if (container) {
      const rowTop = index * ROW_HEIGHT;
      const rowBottom = rowTop + ROW_HEIGHT;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + TABLE_HEIGHT;

      if (rowTop < viewTop) {
        container.scrollTop = rowTop;
      } else if (rowBottom > viewBottom) {
        container.scrollTop = rowBottom - TABLE_HEIGHT;
      }
    }

    pendingScrollToKeyRef.current = "";
  }, [filteredRows]);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const selectRow = useCallback((row) => {
    if (!row) return;
    setSelectedRowKey(row._key);
  }, []);

  const handlePrint = useCallback(async () => {
    if (!selectedPart.partNum) {
      await dialog.warning("Please select a part");
      return;
    }

    try {
      setPrinting(true);

      const partData = await getPart({
        partNumber: selectedPart.partNum,
        rev: selectedPart.rev,
        revMinor: selectedPart.revMinor,
      });

      const normalizedRows = (partData || []).map((row, index) => ({
        _key: `${row.dna ?? ""}-${index}`,
        DNA: row.dna,
        DimensionType: row.dimensionType,
        Unit: row.unit,
        Nominal: row.nominal,
        Min: row.min,
        Max: row.max,
        ToleranceClass: row.toleranceClass,
        InProcess: row.inProcess,
        MethodOfRecording: row.methodOfRecording,
        SpecialInstructions: row.specialInstructions,
        WIATPNumber: row.wiatpNumber,
        Gages: row.gages,
        AdditionalInfo: row.additionalInfo,
        MomOpNumber: row.momOpNumber,
      }));

      setReportRows(normalizedRows);
      setReportTitle(
        `${selectedPart.partNum.toUpperCase()} Rev ${selectedPart.rev}-${selectedPart.revMinor}`
      );
      setShowPreview(true);
    } catch (error) {
      console.error("Failed to build print preview:", error);
      await dialog.error(error?.message || "Failed to build print preview.");
    } finally {
      if (mountedRef.current) {
        setPrinting(false);
      }
    }
  }, [dialog, selectedPart]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;

      if (filteredRows.length > 0) {
        const firstMatch = filteredRows[0];
        setSelectedRowKey(firstMatch._key);
        pendingScrollToKeyRef.current = firstMatch._key;
      }

      if (searchInputRef.current) {
        searchInputRef.current.select();
      }
    },
    [filteredRows]
  );

  const handleDelete = useCallback(async () => {
    if (!selectedPart.partNum) return;

    const confirmed = await dialog.confirm({
      title: "Delete Part",
      message: `Are you sure you wish to delete ${selectedPart.partNum} Rev ${selectedPart.rev}-${selectedPart.revMinor}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "error",
    });

    if (!confirmed) return;

    try {
      const success = await deleteMafiaTable({
        partnum: selectedPart.partNum,
        revMajor: selectedPart.rev,
        revMinor: selectedPart.revMinor,
      });

      if (success) {
        await dialog.success("Part successfully deleted.");
        setShowPreview(false);
        setReportRows([]);
        setReportTitle("");
        setSelectedRowKey("");
        await loadAllParts(true);

        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      } else {
        await dialog.error(
          "There was an error in deleting this part. Please try again or contact technical support."
        );
      }
    } catch (error) {
      console.error("Delete failed:", error);
      await dialog.error(
        "There was an error in deleting this part. Please try again or contact technical support."
      );
    }
  }, [dialog, loadAllParts, selectedPart]);

  const handlePopupPrint = useCallback(() => {
    const printContents = previewPrintRef.current?.innerHTML;
    if (!printContents) return;

    const printWindow = window.open("", "_blank", "width=1400,height=900");
    if (!printWindow) return;

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #000;
            }
            h4 {
              margin: 0 0 6px 0;
            }
            .print-meta {
              margin-bottom: 14px;
              color: #555;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: auto;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #999;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
              white-space: normal;
              word-break: break-word;
            }
            th {
              background: #eee;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  }, [reportTitle]);

  const downloadPreviewHtml = useCallback(() => {
    const printContents = previewPrintRef.current?.innerHTML;
    if (!printContents) return;

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${reportTitle}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #000;
            }
            h4 {
              margin: 0 0 6px 0;
            }
            .print-meta {
              margin-bottom: 14px;
              color: #555;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: auto;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #999;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
              white-space: normal;
              word-break: break-word;
            }
            th {
              background: #eee;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedPart.partNum}_Rev_${selectedPart.rev}_${selectedPart.revMinor}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }, [reportTitle, selectedPart]);

  return (
    <div className="print-page">
      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="print-toolbar-main">
                <div className="print-toolbar-main-left">
                  <Button color="soft-dark" className="print-action-btn" onClick={onBack}>
                    <i className="ri-arrow-left-line print-btn-icon me-1" />
                    Back
                  </Button>
                </div>

                <div className="print-toolbar-main-right">
                  <InputGroup className="print-search-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="border-start-0"
                      placeholder="Search part number"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="print-toolbar-utility">
                <Button
                  color="primary"
                  className="print-action-btn print-utility-btn"
                  onClick={handlePrint}
                  disabled={printing || !selectedPart.partNum}
                >
                  <i className="ri-printer-line print-btn-icon me-1" />
                  {printing ? "Preparing..." : "Build Print Preview"}
                </Button>

                <Button
                  color="secondary"
                  className="print-action-btn print-utility-btn"
                  onClick={() => loadAllParts(true)}
                  disabled={refreshing}
                >
                  <i className="ri-refresh-line print-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>

                {releaser && (
                  <Button
                    color="danger"
                    outline
                    className="print-action-btn print-utility-btn"
                    onClick={handleDelete}
                    disabled={!selectedPart.partNum}
                  >
                    <i className="ri-delete-bin-6-line print-btn-icon me-1" />
                    Delete Part
                  </Button>
                )}

                <Badge color="light" className="print-queue-badge">
                  Queue Size: {filteredRows.length}
                </Badge>

                <div className="print-info-pill">
                  <span className="print-info-label">Selected Part</span>
                  <span
                    className="print-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="print-info-pill">
                  <span className="print-info-label">Revision</span>
                  <span className="print-info-value">
                    {selectedRow
                      ? `${selectedRow.printRevision ?? 0}.${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="print-info-pill">
                  <span className="print-info-label">Preview State</span>
                  <span className="print-info-value">
                    {showPreview ? "Ready" : "Not Generated"}
                  </span>
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="print-table-shell">
                <div
                  ref={tableScrollRef}
                  className="print-table-scroll"
                  onScroll={handleScroll}
                >
                  <Table responsive={false} className="align-middle mb-0 print-table">
                    <thead>
                      <tr>
                        <th className="print-col-select">Select</th>
                        <th>Part Number</th>
                        <th>Print Revision</th>
                        <th>MAFIA Revision</th>
                        <th>Created By</th>
                        <th>Time Stamp</th>
                        <th>Verified</th>
                        <th>Released</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="text-center py-5 text-muted">
                            Loading parts...
                          </td>
                        </tr>
                      ) : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-5 text-muted">
                            No parts found.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {topSpacerHeight > 0 && (
                            <tr style={{ height: `${topSpacerHeight}px` }}>
                              <td colSpan={8} style={{ padding: 0, border: 0 }} />
                            </tr>
                          )}

                          {visibleRows.map((row) => {
                            const isSelected = row._key === selectedRowKey;

                            return (
                              <tr
                                key={row._key}
                                tabIndex={0}
                                onClick={() => selectRow(row)}
                                className={`print-data-row ${isSelected ? "selected" : ""}`}
                                style={{ height: `${ROW_HEIGHT}px` }}
                              >
                                <td className="print-col-select">
                                  <Input
                                    type="radio"
                                    checked={isSelected}
                                    onChange={() => selectRow(row)}
                                    name="selected-print-row"
                                  />
                                </td>
                                <td>
                                  <div className="fw-semibold text-dark">{row.partNumber}</div>
                                </td>
                                <td>{row.printRevision}</td>
                                <td>{row.mafiaRevision}</td>
                                <td>{row.createdBy}</td>
                                <td>{row.timeStampDisplay}</td>
                                <td>
                                  <StatusBadge value={row.verified} />
                                </td>
                                <td>
                                  <StatusBadge value={row.released} />
                                </td>
                              </tr>
                            );
                          })}

                          {bottomSpacerHeight > 0 && (
                            <tr style={{ height: `${bottomSpacerHeight}px` }}>
                              <td colSpan={8} style={{ padding: 0, border: 0 }} />
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={showPreview}
        toggle={() => setShowPreview(false)}
        size="xl"
        centered
        scrollable
        className="print-preview-modal"
      >
        <ModalHeader toggle={() => setShowPreview(false)}>
          Print Preview - {reportTitle}
        </ModalHeader>

        <ModalBody>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button color="dark" className="print-action-btn" onClick={handlePopupPrint}>
              <i className="ri-printer-line print-btn-icon me-1" />
              Print This Table
            </Button>

            <Button color="secondary" className="print-action-btn" onClick={downloadPreviewHtml}>
              <i className="ri-download-line print-btn-icon me-1" />
              Save Table File
            </Button>

            <Button
              color="light"
              className="print-action-btn border"
              onClick={() => setShowPreview(false)}
            >
              <i className="ri-close-line print-btn-icon me-1" />
              Close
            </Button>
          </div>

          <div ref={previewPrintRef} className="popup-print-area">
            <div className="mb-3">
              <h4 className="mb-1">{reportTitle}</h4>
              <div className="print-meta">
                Part: {selectedPart.partNum} | Rev: {selectedPart.rev}.{selectedPart.revMinor}
              </div>
            </div>

            <div className="print-table-shell">
              <div
                className="print-table-scroll print-table-scroll-preview"
                style={{ maxHeight: `${PREVIEW_TABLE_HEIGHT}px` }}
              >
                <Table
                  responsive={false}
                  className="align-middle mb-0 print-table print-preview-table"
                >
                  <thead>
                    <tr>
                      <th>DNA</th>
                      <th>Dimension Type</th>
                      <th>Unit</th>
                      <th>Nominal</th>
                      <th>Min</th>
                      <th>Max</th>
                      <th>Tolerance Class</th>
                      <th>In Process</th>
                      <th>Method Of Recording</th>
                      <th>Special Instructions</th>
                      <th>WI or ATP Number</th>
                      <th>Gages</th>
                      <th>Additional Info</th>
                      <th>Mom Op #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="text-center py-5 text-muted">
                          No report data found.
                        </td>
                      </tr>
                    ) : (
                      reportRows.map((row) => (
                        <tr key={row._key} className="print-data-row">
                          <td>{row.DNA}</td>
                          <td>{row.DimensionType}</td>
                          <td>{row.Unit}</td>
                          <td>{row.Nominal}</td>
                          <td>{row.Min}</td>
                          <td>{row.Max}</td>
                          <td>{row.ToleranceClass}</td>
                          <td>{row.InProcess}</td>
                          <td>{row.MethodOfRecording}</td>
                          <td>{row.SpecialInstructions}</td>
                          <td>{row.WIATPNumber}</td>
                          <td>{row.Gages}</td>
                          <td>{row.AdditionalInfo}</td>
                          <td>{row.MomOpNumber}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <style>{`
        .print-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .print-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .print-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .print-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .print-search-group {
          width: 100%;
        }

        .print-action-btn {
          border-radius: 0.65rem;
          font-weight: 600;
          padding: 0.42rem 0.8rem;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .print-utility-btn {
          min-width: 120px;
        }

        .print-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: rgba(15, 23, 42, 0.08);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: rgba(15, 23, 42, 0.14);
          color: #0f172a;
          border-color: rgba(15, 23, 42, 0.12);
        }

        .print-queue-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #0f172a;
          background: #f8fafc;
        }

        .print-info-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 38px;
          padding: 0.45rem 0.75rem;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 0.65rem;
          background: #f8fafc;
          max-width: 300px;
        }

        .print-info-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          color: #64748b;
          white-space: nowrap;
        }

        .print-info-value {
          font-size: 0.9rem;
          font-weight: 700;
          color: #0f172a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .print-table-shell {
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 1rem;
          overflow: hidden;
          background: #fff;
        }

        .print-table-scroll {
          max-height: ${TABLE_HEIGHT}px;
          overflow: auto;
          will-change: transform;
        }

        .print-table-scroll-preview {
          max-height: ${PREVIEW_TABLE_HEIGHT}px;
        }

        .print-table {
          min-width: 1120px;
        }

        .print-preview-table {
          min-width: 1800px;
        }

        .print-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #0f172a;
          color: #f8fafc;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 800;
          padding: 0.95rem 0.9rem;
          border-bottom: 0;
          white-space: nowrap;
        }

        .print-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #334155;
          vertical-align: middle;
          white-space: nowrap;
        }

        .popup-print-area .print-table tbody td {
          vertical-align: top;
        }

        .print-col-select {
          width: 72px;
          text-align: center;
        }

        .print-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .print-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .print-data-row:hover {
          background: #f5f9ff;
        }

        .print-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .print-status {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.65rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
        }

        .print-status.yes {
          background: rgba(25, 135, 84, 0.12);
          color: #198754;
        }

        .print-status.no {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .print-status.na {
          background: rgba(100, 116, 139, 0.12);
          color: #64748b;
        }

        .print-preview-modal .modal-dialog {
          max-width: 95vw;
        }

        .print-preview-modal .modal-body {
          padding: 1rem 1rem 1.25rem;
        }

        .print-meta {
          color: #64748b;
          font-size: 0.95rem;
          font-weight: 500;
        }

        @media (max-width: 1199.98px) {
          .print-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .print-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .print-table-scroll,
          .print-table-scroll-preview {
            max-height: 460px;
          }

          .print-info-pill {
            max-width: 100%;
          }

          .print-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function StatusBadge({ value }) {
  if (value === true) {
    return (
      <span className="print-status yes">
        <i className="ri-checkbox-circle-fill" /> Yes
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="print-status no">
        <i className="ri-close-circle-fill" /> No
      </span>
    );
  }

  return <span className="print-status na">—</span>;
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function normalizePartRow(item) {
  const partNumber = item.partNumber ?? "";
  const printRevision = item.printRevision ?? 0;
  const mafiaRevision = item.mafiaRevision ?? 0;
  const createdBy = item.createdBy ?? "";
  const timeStamp = item.timeStamp ?? "";
  const timeStampDisplay = formatDateTime(timeStamp);

  const normalized = {
    partNumber,
    printRevision,
    mafiaRevision,
    createdBy,
    timeStamp,
    timeStampDisplay,
    verified: item.verified,
    released: item.released,
  };

  normalized._key = getRowKey(normalized);
  normalized._search = [
    String(partNumber),
    String(printRevision),
    String(mafiaRevision),
    String(createdBy),
    String(timeStampDisplay),
  ]
    .join("|")
    .toUpperCase();

  return normalized;
}

async function getAllParts() {
  const response = await api.get("mafia/all-parts");

  return (response.data || []).map(normalizePartRow);
}

async function getPart(request) {
  const response = await api.post("mafia/part", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  return response.data;
}

async function deleteMafiaTable(request) {
  const response = await api.delete("mafia/deletemafiatable", {
    params: {
      partnum: request.partnum,
      revMajor: request.revMajor,
      revMinor: request.revMinor,
    },
  });

  return response.data;
}